import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const convertBound = (bounds: google.maps.LatLngBounds) => {
  const northEast = bounds?.getNorthEast();
  const southWest = bounds?.getSouthWest();

  return {
    northEast: { lat: northEast?.lat(), lng: northEast?.lng() },
    southWest: { lat: southWest?.lat(), lng: southWest?.lng() },
  };
};

export function parseMapUrl(
  str: string,
): { lat: number; lng: number; zoom: number } | null {
  const match = str.match(
    /@(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?),(\d+(?:\.\d+)?)z/,
  );
  if (!match) return null;

  const [, lat, lng, zoom] = match;
  return {
    lat: parseFloat(lat),
    lng: parseFloat(lng),
    zoom: parseFloat(zoom),
  };
}

export function getScreenDimensions(inch: number, aspectW = 16, aspectH = 9) {
  const diagonalMm = inch * 25.4;
  const aspectRatio = aspectW / aspectH;

  const heightMm = Math.sqrt(diagonalMm ** 2 / (aspectRatio ** 2 + 1));
  const widthMm = heightMm * aspectRatio;

  return {
    width: Math.round(widthMm), // in mm
    height: Math.round(heightMm), // in mm
  };
}

// export function convertCommonCDToGroupObject(data: CommonCDType[]): Record<string, CommonCDType[]> | null {
//   if(!data || !data?.length) return null;
//   // eslint-disable-next-line @typescript-eslint/no-explicit-any
//   return data?.reduce((acc: any, item) => {
//     const { grp_code } = item;
//     if (!acc[grp_code]) {
//       acc[grp_code] = [];
//     }
//     acc[grp_code].push(item);
//     return acc;
//   }, {});
// }

export function groupDataByKey<T>(
  data: T[],
  key: keyof T,
): Record<string, T[]> | null {
  if (!Array.isArray(data) || data.length === 0 || !key) {
    return null;
  }

  return data.reduce<Record<string, T[]>>((acc, item) => {
    const groupKey = item[key];

    if (groupKey == null) {
      return acc;
    }

    const groupKeyStr = String(groupKey);
    if (!acc[groupKeyStr]) {
      acc[groupKeyStr] = [];
    }
    acc[groupKeyStr].push(item);
    return acc;
  }, {});
}

export function getSecondsBetween(fromTime: string, toTime: string): number {
  const [fromH, fromM] = fromTime.split(":").map(Number);
  const [toH, toM] = toTime.split(":").map(Number);

  const fromSeconds = fromH * 3600 + fromM * 60;
  const toSeconds = toH * 3600 + toM * 60;

  return toSeconds >= fromSeconds
    ? toSeconds - fromSeconds
    : 24 * 3600 - fromSeconds + toSeconds;
}

export function dateFormatted(date: string) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}.${month}.${day} ${hours}:${minutes}`;
}

export function formatNumber(number?: number | null) {
  if (!number) return number;
  return new Intl.NumberFormat("ko-KR").format(number);
}
export function formatPrice(value?: number): string {
  if (typeof value !== "number") return "-";
  const truncatedValue = Math.trunc(value); // Truncate to integer without rounding
  return new Intl.NumberFormat("ko-KR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(truncatedValue);
}

export function formatDate(
  dateInput?: string | Date,
  type: "dash" | "dot" | "second" | "minute" = "dot",
): string {
  if (!dateInput) return "-";

  try {
    const d =
      dateInput instanceof Date ? dateInput : convertUtcToLocal(dateInput);
    if (!d || isNaN(d.getTime())) return "-";
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const seconds = String(d.getSeconds()).padStart(2, "0");

    if (type === "second") {
      return `${year}.${month}.${day} ${hours}:${minutes}:${seconds}`;
    } else if (type === "minute") {
      return `${year}.${month}.${day} ${hours}:${minutes}`;
    } else if (type === "dash") {
      return `${year}-${month}-${day}`;
    } else {
      // default "dot" type
      return `${year}.${month}.${day}`;
    }
  } catch (error) {
    console.error("Error formatting date:", error);
    return "-";
  }
}
export const formatKoreanDate = (
  date: Date | null,
  tunit: (key: string) => string,
) => {
  if (!date) return "";
  const yyyy = date.getFullYear();
  const mm = date.getMonth() + 1;
  const dd = date.getDate();
  return `${yyyy}${tunit("units.year")} ${mm}${tunit(
    "units.month",
  )} ${dd}${tunit("units.day")}`;
};
export function formatOperationHours(
  startTime: string,
  endTime: string,
  t: (key: string) => string,
): string {
  const start = startTime.slice(0, 5);
  const end = endTime.slice(0, 5);

  const is24Hours = start === "00:00";
  const isEndOfDay = end === "23:59";

  if (is24Hours && isEndOfDay) {
    return t("operation.hours24");
  }

  return `${start} - ${end}`;
}
export type CommonCdListLike =
  | { data?: { grp_sub_cd?: string; common_cd_name?: string }[] }
  | undefined;
export function getCommonCdName(
  list: CommonCdListLike,
  grp_sub_cd: string | undefined,
): string {
  return (
    list?.data?.find((item) => item.grp_sub_cd === grp_sub_cd)
      ?.common_cd_name ?? "----"
  );
}
export function isEmptyObject(obj: unknown): obj is Record<string, never> {
  return (
    typeof obj === "object" &&
    obj !== null &&
    Object.keys(obj).length === 0 &&
    obj.constructor === Object
  );
}

export const mathRound = (num: string) => {
  const floatNum = parseFloat(num.replace(/,/g, ""));
  const integerPart = Math.floor(floatNum);
  const decimalPart = floatNum - integerPart;
  const rounded = decimalPart >= 0.5 ? integerPart + 1 : integerPart;
  return rounded.toLocaleString();
};

export const truncateNumber = (num: string) => {
  const floatNum = parseFloat(num.replace(/,/g, ""));
  if (isNaN(floatNum)) return "0";
  const truncated = Math.trunc(floatNum);
  return truncated.toLocaleString();
};
export function parseDueDateValue(input: string) {
  // Converts YYYY.MM.DD까지, YYYY.MM.DD, YYYY-MM-DD, or YYYY-MM-DDTHH:mm:ss to YYYY-MM-DD
  const matchDot = input.match(/^(\d{4})\.(\d{2})\.(\d{2})/);
  if (matchDot) {
    return `${matchDot[1]}-${matchDot[2]}-${matchDot[3]}`;
  }
  const matchDash = input.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (matchDash) {
    return input;
  }
  const matchIso = input.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (matchIso) {
    return `${matchIso[1]}-${matchIso[2]}-${matchIso[3]}`;
  }
  return "";
}

// Format input as YYYY.MM.DD까지 while typing, and convert to YYYY-MM-DD on update
export const formatDueDateInput = (value: string) => {
  const digits = value.replace(/[^0-9]/g, "").slice(0, 8);
  let formatted = digits;

  if (digits.length >= 4) {
    formatted = digits.slice(0, 4) + "." + digits.slice(4);
  }
  if (digits.length >= 6) {
    formatted = formatted.slice(0, 7) + "." + formatted.slice(7);
  }
  return formatted;
};

export const convertSecondsToMinutes = (
  seconds: number,
  t: (key: string) => string,
): string => {
  const minutes = Math.floor(seconds / 60);
  const remaining = seconds % 60;
  return `${minutes}${t("time.minute")} ${remaining}${t("time.second")}`;
};

export const calcTotalPages = (pagination?: {
  total?: number;
  page_size?: number;
}): number => {
  const total = pagination?.total ?? 1;
  const pageSize = pagination?.page_size ?? 1;

  if (total > pageSize) {
    return Math.ceil(total / pageSize);
  }

  return 1;
};

/**
 * Convert ISO string (UTC) to Date object in local timezone
 * @param isoString - ISO string in UTC format (e.g., "2025-11-28T03:40:28.707Z")
 * @returns Date object in local timezone
 */
export function convertUtcToLocal(isoString: string): Date {
  // Be tolerant: accept undefined/null and many ISO-like formats.
  if (!isoString) {
    // Return invalid date so callers can handle it (formatDate checks isNaN)
    return new Date(NaN);
  }

  let s = String(isoString).trim();

  // Replace space between date and time with 'T' (common non-ISO format)
  s = s.replace(
    /^(\d{4}-\d{2}-\d{2})[ \t]+(\d{2}:\d{2}:\d{2}(?:\.\d+)?)$/,
    "$1T$2",
  );

  // Normalize fractional seconds to milliseconds (3 digits)
  const fracMatch = s.match(/\.(\d+)(?=(?:Z|[+\-]|$))/);
  if (fracMatch) {
    const frac = fracMatch[1].slice(0, 3).padEnd(3, "0");
    s = s.replace(/\.(\d+)(?=(?:Z|[+\-]|$))/, `.${frac}`);
  }

  // Normalize timezone offsets: +07 or +0700 -> +07:00
  s = s.replace(/([+\-]\d{2})(\d{2})$/, "$1:$2");
  s = s.replace(/([+\-]\d{2})$/, "$1:00");

  // If no timezone present, treat as UTC by appending 'Z'
  if (!/[zZ]|[+\-]\d{2}:?\d{2}$/.test(s)) {
    s = `${s}Z`;
  }

  // Try JS Date parsing
  const date = new Date(s);
  if (!isNaN(date.getTime())) return date;

  // Fallback: try parsing original string with Date.parse (handles some variants)
  const parsed = Date.parse(String(isoString));
  if (!isNaN(parsed)) return new Date(parsed);

  // As a last resort, return an invalid Date (caller should check)
  return new Date(NaN);
}

export function calculateDay(startDate?: string, endDate?: string): number {
  if (!startDate || !endDate) return 1;

  try {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const timeDiff = end.getTime() - start.getTime();
    const dayDiff = Math.floor(timeDiff / (1000 * 3600 * 24)) + 1;
    return Math.max(1, dayDiff);
  } catch {
    return 1;
  }
}
